<template>
  <a-modal
    v-model:open="modalVisible"
    :footer="null"
    :closable="true"
    :maskClosable="true"
    :width="420"
    centered
    class="login-modal"
    @cancel="handleClose"
  >
    <div class="login-modal-content">
      <!-- Logo和标题 -->
      <div class="login-header">
        <div class="logo">
          <span class="logo-icon"><AppIcon name="film" /></span>
          <span class="logo-text">{{ SITE_FULL_NAME }}</span>
        </div>
        <h2 class="title">欢迎使用</h2>
        <p class="subtitle">请选择登录方式</p>
      </div>

      <!-- 登录方式切换 -->
      <a-tabs v-model:activeKey="loginMode" centered class="login-tabs">
        <!-- 二维码登录（移动端隐藏） -->
        <a-tab-pane v-if="!isMobileDevice" key="qrcode" tab="扫码登录">
          <div class="qrcode-container">
            <div v-if="qrLoading" class="qrcode-loading">
              <a-spin size="large" />
              <p>正在获取二维码...</p>
            </div>
            <div v-else-if="qrExpired" class="qrcode-expired" @click="refreshQRCode">
              <ReloadOutlined class="refresh-icon" />
              <p>二维码已过期，点击刷新</p>
            </div>
            <div v-else class="qrcode-wrapper">
              <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="登录二维码" class="qrcode-image" />
              <p class="qrcode-tip">{{ qrStatusText }}</p>
            </div>
          </div>
        </a-tab-pane>

        <!-- 手机号登录 -->
        <a-tab-pane key="phone" tab="手机号登录">
          <a-form
            ref="phoneFormRef"
            :model="phoneForm"
            :rules="phoneRules"
            layout="vertical"
            @finish="handlePhoneLogin"
          >
            <a-form-item name="phone" label="手机号">
              <a-input
                v-model:value="phoneForm.phone"
                placeholder="请输入手机号"
                size="large"
                :prefix="h(MobileOutlined)"
                :maxlength="11"
              />
            </a-form-item>

            <!-- 图形验证码（发送短信前需要先验证） -->
            <a-form-item v-if="showCaptcha" label="图形验证码">
              <div class="captcha-wrapper">
                <a-input
                  v-model:value="captchaForm.code"
                  placeholder="请输入图形验证码"
                  size="large"
                  :maxlength="4"
                  class="captcha-input"
                />
                <div class="captcha-image-wrapper" @click="refreshCaptcha">
                  <img 
                    v-if="captchaForm.image" 
                    :src="captchaForm.image" 
                    alt="图形验证码" 
                    class="captcha-image"
                  />
                  <a-spin v-else size="small" />
                </div>
              </div>
              <div class="captcha-tip">点击图片刷新验证码</div>
            </a-form-item>

            <a-form-item name="code" label="短信验证码">
              <div class="code-input-wrapper">
                <a-input
                  v-model:value="phoneForm.code"
                  placeholder="请输入短信验证码"
                  size="large"
                  :prefix="h(SafetyOutlined)"
                  :maxlength="6"
                  class="code-input"
                />
                <a-button
                  :disabled="!canSendCode"
                  :loading="sendingCode"
                  size="large"
                  class="send-code-btn"
                  @click="handleSendCode"
                >
                  {{ sendCodeButtonText }}
                </a-button>
              </div>
            </a-form-item>

            <a-form-item>
              <a-button
                type="primary"
                html-type="submit"
                size="large"
                block
                :loading="loading"
              >
                登录
              </a-button>
            </a-form-item>
          </a-form>
        </a-tab-pane>
      </a-tabs>

      <!-- 用户协议 -->
      <div class="agreement">
        登录即表示同意
        <a href="javascript:;">《用户协议》</a>
        和
        <a href="javascript:;">《隐私政策》</a>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
/**
 * 登录弹窗组件
 * 支持二维码登录和手机号登录（基于 CSD SDK）
 */
import { ref, reactive, h, watch, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { MobileOutlined, SafetyOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import type { Rule } from 'ant-design-vue/es/form';
import { SITE_FULL_NAME } from '@shared/constants/brand';
import { useUserStore } from '@/stores/user';
import { useLoginModal } from '@/composables/useLoginModal';
import { getCSDClient, isMobile as checkIsMobile } from '@/lib/csd-adapter';
import AppIcon from '@/components/common/AppIcon.vue';

const router = useRouter();
const userStore = useUserStore();
const { visible, closeLoginModal, handleLoginSuccess, getRedirectPath } = useLoginModal();

// 获取 CSD SDK 客户端
const csd = getCSDClient();

// 是否为移动端浏览器（移动端不显示扫码登录）
const isMobileDevice = checkIsMobile();

// 弹窗显示状态
const modalVisible = ref(false);

// 登录方式：qrcode / phone（移动端默认手机号登录）
const loginMode = ref<'qrcode' | 'phone'>(isMobileDevice ? 'phone' : 'qrcode');

// 加载状态
const loading = ref(false);

// ============================================
// 二维码登录相关
// ============================================
const qrCodeUrl = ref('');
const qrLoading = ref(false);
const qrExpired = ref(false);
const qrScanned = ref(false);
const qrStatusText = computed(() => {
  if (loading.value) return '正在登录，请稍候...';
  if (qrScanned.value) return '已扫码，请在微信中确认登录';
  return '请使用微信扫描二维码登录';
});

/**
 * 获取二维码并开始轮询
 */
async function startQRLogin() {
  if (!csd) {
    message.error('CSD SDK 未初始化');
    return;
  }

  qrLoading.value = true;
  qrExpired.value = false;
  qrScanned.value = false;

  try {
    const authService = csd.getAuthService();
    
    // 使用 SDK 的 startQRLogin 方法，它会自动处理轮询
    const qrResult = await authService.startQRLogin((status) => {
      // 状态回调
      if (status === 'confirmed') {
        // 登录成功，获取 Token
        qrScanned.value = true;
        const token = authService.getToken();
        if (token) {
          handleCsdLoginSuccess();
        }
      } else if (status === 'scanned') {
        qrScanned.value = true;
      } else if (status === 'expired' || status === 'cancelled') {
        qrExpired.value = true;
        qrScanned.value = false;
      }
    });
    
    qrCodeUrl.value = qrResult.url;
    qrLoading.value = false;
  } catch (error) {
    qrLoading.value = false;
    message.error('获取二维码失败，请重试');
    console.error('获取二维码失败:', error);
  }
}

/**
 * 停止二维码轮询
 */
function stopQRPolling() {
  if (!csd) return;
  
  // 调用 SDK 的 stopQRLogin 方法停止轮询
  const authService = csd.getAuthService();
  authService.stopQRLogin();
}

/**
 * 刷新二维码
 */
function refreshQRCode() {
  stopQRPolling();
  startQRLogin();
}

// ============================================
// 手机号登录相关
// ============================================
const phoneForm = reactive({
  phone: '',
  code: '',
});

// 图形验证码相关
const captchaForm = reactive({
  key: '',      // 验证码 key
  image: '',    // 验证码图片 base64
  code: '',     // 用户输入的验证码
});
const showCaptcha = ref(false);  // 是否显示图形验证码

const countdown = ref(0);
const sendingCode = ref(false);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

// 计算属性：是否可以发送验证码
const canSendCode = computed(() => {
  // 手机号格式正确 + 不在倒计时中 + 不在发送中
  const phoneValid = /^1[3-9]\d{9}$/.test(phoneForm.phone);
  // 如果显示了图形验证码，还需要验证图形验证码已填写
  if (showCaptcha.value) {
    return phoneValid && countdown.value <= 0 && !sendingCode.value && captchaForm.code.length === 4;
  }
  return phoneValid && countdown.value <= 0 && !sendingCode.value;
});

// 计算属性：发送按钮文字
const sendCodeButtonText = computed(() => {
  if (countdown.value > 0) {
    return `${countdown.value}s`;
  }
  return showCaptcha.value ? '发送验证码' : '获取验证码';
});

// 手机号验证规则
const phoneRules: Record<string, Rule[]> = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' },
  ],
  code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { pattern: /^\d{4,6}$/, message: '验证码格式不正确', trigger: 'blur' },
  ],
};

/**
 * 获取图形验证码
 */
async function refreshCaptcha() {
  if (!csd) return;
  
  try {
    const authService = csd.getAuthService();
    const result = await authService.getImageCaptcha();
    
    if (result) {
      captchaForm.key = result.captcha_key;
      // 图片可能是 base64 或 URL
      captchaForm.image = result.captcha_image_content.startsWith('data:') 
        ? result.captcha_image_content 
        : `data:image/png;base64,${result.captcha_image_content}`;
      captchaForm.code = '';
    }
  } catch (error) {
    console.error('获取图形验证码失败:', error);
    message.error('获取图形验证码失败');
  }
}

/**
 * 发送验证码
 */
async function handleSendCode() {
  if (!csd || !phoneForm.phone) return;
  if (sendingCode.value || countdown.value > 0) return;

  // 验证手机号格式
  if (!/^1[3-9]\d{9}$/.test(phoneForm.phone)) {
    message.error('请输入正确的手机号');
    return;
  }

  // 如果还没显示图形验证码，先获取并显示
  if (!showCaptcha.value) {
    showCaptcha.value = true;
    await refreshCaptcha();
    return;
  }

  // 验证图形验证码
  if (!captchaForm.code || captchaForm.code.length !== 4) {
    message.error('请输入4位图形验证码');
    return;
  }

  sendingCode.value = true;

  try {
    const authService = csd.getAuthService();
    
    // 使用带图形验证码的发送方法
    await authService.sendVerifyCodeWithCaptcha({
      phone: phoneForm.phone,
      captcha_key: captchaForm.key,
      captcha_code: captchaForm.code,
      smsType: 'login',
    });
    
    message.success('验证码已发送');
    
    // 隐藏图形验证码（发送成功后不再需要）
    showCaptcha.value = false;
    captchaForm.key = '';
    captchaForm.image = '';
    captchaForm.code = '';
    
    // 开始倒计时
    countdown.value = 60;
    countdownTimer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(countdownTimer!);
        countdownTimer = null;
      }
    }, 1000);
  } catch (error: unknown) {
    const errorMsg = error instanceof Error ? error.message : '发送验证码失败';
    message.error(errorMsg);
    console.error('发送验证码失败:', error);
    
    // 发送失败，刷新图形验证码
    await refreshCaptcha();
  } finally {
    sendingCode.value = false;
  }
}

/**
 * 手机号登录
 */
async function handlePhoneLogin() {
  if (!csd) {
    message.error('CSD SDK 未初始化');
    return;
  }

  if (loading.value) return;
  loading.value = true;

  try {
    const authService = csd.getAuthService();
    // SDK 的 phoneLogin 方法签名: phoneLogin(params: { phone: string, code: string })
    // 登录成功后 Token 会自动保存到 AuthStore
    await authService.phoneLogin({
      phone: phoneForm.phone,
      code: phoneForm.code,
    });

    // 获取登录后的 Token
    const token = authService.getToken();
    if (token) {
      await handleCsdLoginSuccess();
    } else {
      message.error('登录失败，请重试');
    }
  } catch (error: unknown) {
    const errorMsg = error instanceof Error ? error.message : '登录失败';
    message.error(errorMsg);
    console.error('手机号登录失败:', error);
  } finally {
    loading.value = false;
  }
}

// ============================================
// 通用登录成功处理
// ============================================

/**
 * CSD 登录成功后，直接拉取本地用户信息
 */
async function handleCsdLoginSuccess() {
  if (!csd) {
    message.error('CSD SDK 未初始化');
    return;
  }

  try {
    loading.value = true;
    // 注意：积分获取由 CreditsDisplay 组件负责，登录成功后会自动刷新
    await userStore.csdLogin();
    
    message.success('登录成功');

    // 获取重定向地址
    const redirect = getRedirectPath();
    
    // 触发登录成功回调
    handleLoginSuccess();
    
    // 如果有重定向路径，跳转
    if (redirect) {
      router.push(redirect);
    }
  } catch (error) {
    const errorMsg = error instanceof Error && error.message ? error.message : '登录失败，请重试';
    message.error(errorMsg);
    console.error('CSD Token 拉取本地用户失败:', error);
  } finally {
    loading.value = false;
  }
}

// ============================================
// 生命周期
// ============================================

// 同步外部状态
watch(visible, (val) => {
  modalVisible.value = val;
  if (val && loginMode.value === 'qrcode') {
    // 打开弹窗时自动获取二维码
    startQRLogin();
  }
});

// 同步本地状态到外部
watch(modalVisible, (val) => {
  if (!val) {
    closeLoginModal();
    stopQRPolling();
  }
});

// 切换登录方式时的处理
watch(loginMode, (mode) => {
  if (mode === 'qrcode' && modalVisible.value) {
    startQRLogin();
  } else {
    stopQRPolling();
  }
});

// 组件卸载时清理
onUnmounted(() => {
  stopQRPolling();
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
});

/**
 * 关闭弹窗
 */
function handleClose() {
  closeLoginModal();
  stopQRPolling();
  // 重置状态
  loginMode.value = 'qrcode';
  phoneForm.phone = '';
  phoneForm.code = '';
  // 重置图形验证码状态
  showCaptcha.value = false;
  captchaForm.key = '';
  captchaForm.image = '';
  captchaForm.code = '';
  qrCodeUrl.value = '';
  qrExpired.value = false;
  qrScanned.value = false;
}
</script>

<style lang="scss" scoped>
.login-modal {
  :deep(.ant-modal-content) {
    background: var(--bg-secondary);
    border-radius: 16px;
    padding: 0;
  }
  
  :deep(.ant-modal-body) {
    padding: 0;
  }
  
  :deep(.ant-modal-close) {
    color: var(--text-tertiary);
    
    &:hover {
      color: var(--text-primary);
    }
  }
}

.login-modal-content {
  padding: 40px 32px;
}

.login-header {
  text-align: center;
  margin-bottom: 24px;

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;

    .logo-icon {
      font-size: 28px;
    }

    .logo-text {
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary);
    }
  }

  .title {
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .subtitle {
    font-size: 14px;
    color: var(--text-tertiary);
    margin: 0;
  }
}

.login-tabs {
  :deep(.ant-tabs-nav) {
    margin-bottom: 24px;
  }
}

/* 二维码登录样式 */
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  min-height: 240px;
}

.qrcode-loading,
.qrcode-expired {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  
  p {
    margin-top: 16px;
    color: var(--text-tertiary);
    font-size: 14px;
  }
}

.qrcode-expired {
  cursor: pointer;
  
  &:hover {
    background: var(--bg-hover);
  }
  
  .refresh-icon {
    font-size: 32px;
    color: var(--text-tertiary);
  }
}

.qrcode-wrapper {
  text-align: center;
  
  .qrcode-image {
    width: 200px;
    height: 200px;
    border-radius: 8px;
    border: 1px solid var(--border-color);
  }
  
  .qrcode-tip {
    margin-top: 16px;
    color: var(--text-tertiary);
    font-size: 14px;
  }
}

/* 手机号登录样式 */
.captcha-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  
  .captcha-input {
    flex: 1;
  }
  
  .captcha-image-wrapper {
    width: 120px;
    height: 40px;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    background: var(--bg-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-color);
    
    &:hover {
      border-color: var(--color-primary);
    }
    
    .captcha-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

.captcha-tip {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 4px;
}

.code-input-wrapper {
  display: flex;
  gap: 12px;
  
  .code-input {
    flex: 1;
  }
  
  .send-code-btn {
    width: 120px;
    flex-shrink: 0;
  }
}

/* 用户协议 */
.agreement {
  text-align: center;
  color: var(--text-tertiary);
  font-size: 12px;
  margin-top: 24px;
  
  a {
    color: var(--color-primary);
    
    &:hover {
      text-decoration: underline;
    }
  }
}

/* 深色主题表单样式覆盖 */
:deep(.ant-form-item-label > label) {
  color: var(--text-secondary);
}

:deep(.ant-input-affix-wrapper),
:deep(.ant-input) {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
  color: var(--text-primary);

  &:hover,
  &:focus {
    border-color: var(--color-primary);
  }

  .ant-input-prefix {
    color: var(--text-tertiary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
}

:deep(.ant-btn-primary) {
  height: 44px;
  font-size: 15px;
  font-weight: 500;
}

/* 移动端响应式适配 */
@media (max-width: 480px) {
  .login-modal-content {
    padding: 32px 24px;
  }
  
  .login-header {
    margin-bottom: 20px;
    
    .logo {
      margin-bottom: 16px;
      
      .logo-icon {
        font-size: 24px;
      }
      
      .logo-text {
        font-size: 16px;
      }
    }
    
    .title {
      font-size: 20px;
    }
  }
  
  .code-input-wrapper {
    flex-direction: column;
    
    .send-code-btn {
      width: 100%;
    }
  }
}
</style>
